/* @flow weak */
import humanparser from 'humanparser';
import PropTypes from 'prop-types';
import * as React from 'react';

import { handleSendCodeError } from 'settings/lib/emailDeviceVerificationHelpers';
import { mountModal } from 'lib/modal';
import AuthActions from 'auth/AuthActions';
import Button from 'components/ButtonDeprecated';
import Device from 'entities/Device';
import DeviceActions from 'actions/DeviceActions';
import DividerText from 'components/DividerText';
import EventTracking from 'lib/EventTracking';
import FormDeprecated from 'components/FormDeprecated';
import FormInput from 'components/FormInput';
import GoogleSignIn from 'auth/components/GoogleSignIn';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupDataOps from 'groups/lib/GroupDataOps';
import HelpIcon from 'components/HelpIcon';
import JoinDeviceBlockModal from './JoinDeviceBlockModal';
import JoinFlowGroupStorage from '../data/JoinFlowGroupStorage';
import LinkDeviceVerificationCodeBoxDialog from 'user/components/LinkDeviceVerificationCodeBoxDialog';
import Subscription from 'entities/Subscription';
import ToastActions from 'actions/ToastActions';
import TosText from 'components/TosText';
import UserActions from 'user/UserActions';

import './styles/JoinGroupFormPanel.scss';

class JoinGroupFormPanel extends React.Component<$FlowFixMeProps, $FlowFixMeState> {
  static propTypes = {
    group: PropTypes.instanceOf(Group).isRequired,
  };

  // eslint-disable-next-line @remind/remind/legacy-context
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
  };

  static childContextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    eventProperties: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      i18n: !props.group.isSMSEnabled(),
      medium: props.group.isSMSEnabled() ? null : 'email',
      uidValue: '',
      canSubmit: false,
    };
  }

  getChildContext() {
    return {
      eventProperties: {
        join_method: this.state.medium,
        join_device: this.state.uidValue,
      },
    };
  }

  triggerTosViewEvent = () => {
    EventTracking.trackKey('tos_and_pp.view');
  };

  handleGoogleSuccess = user => {
    if (user.getNeeds().role) {
      this.context.router.push(`/join/role_picker/${this.props.group.getCodeOrError()}`);
      return;
    }

    const onSubscribeSuccess = () => {
      JoinFlowGroupStorage.setJoinedClassName(GroupDataOps.getClassName(this.props.group));
      ToastActions.showSuccessToast('Class joined');
      this.context.router.push(`/classes/${this.props.group.getCodeOrError()}?origin=subscribed`);
    };

    const onSubscribeFailure = error => {
      if (error.code === 'duplicate_membership') {
        ToastActions.showErrorToast(error.message);
        this.context.router.push(`/classes/${this.props.group.getCodeOrError()}?origin=subscribed`);
        return;
      }

      if (error.code === 'information_required') {
        this.context.router.push(`/information_required/${this.props.group.getCodeOrError()}`);
        return;
      }

      ToastActions.showDefaultErrorToast();
    };

    GroupActions.subscribeToGroup(this.props.group).then(onSubscribeSuccess, onSubscribeFailure);

    this.triggerTosViewEvent();
  };

  handleUidChange = event => {
    const { value } = event.target;
    const medium = this.state.i18n || value.includes('@') ? 'email' : 'sms';
    this.setState({
      uidValue: value,
      medium,
    });
  };

  continueEmailFlow = (attr, invalidateForm) => {
    const onFailure = error => {
      if (error.field_errors) {
        const errors = {};
        for (const field of error.field_errors) {
          let fieldName = field['field_name'];
          if (fieldName === 'email') {
            fieldName = 'uid';
          }

          errors[fieldName] = field.messages.join(', ');
        }
        invalidateForm(errors);
      }

      ToastActions.showDefaultErrorToast();
    };

    const human = humanparser.parseName(attr['full_name']);
    const data = {
      prefix: human.salutation,
      first_name: human.firstName,
      last_name: human.lastName,
      password: attr.password,
      email: attr.uid,
      role: '' /* !!DO NOT REMOVE THIS!!! */,
    };

    if (!data['last_name']) {
      invalidateForm({ full_name: 'Name must include first and last names' });
      return;
    }

    const onSuccess = () => {
      GroupActions.subscribeToGroup(this.props.group).then(
        () => {
          JoinFlowGroupStorage.setJoinedClassName(GroupDataOps.getClassName(this.props.group));
          this.context.router.push('/join/role_picker');
        },
        () => {
          this.context.router.push(`/join/role_picker/${this.props.group.getCodeOrError()}`);
        }
      );
    };

    UserActions.create(data).then(onSuccess, onFailure);
  };

  continueSmsFlow = (attr, invalidateForm) => {
    const address = this.state.medium + '://' + attr.uid;
    const onSuccess = response => {
      this.context.router.push({
        pathname: `/join/${this.props.group.getCodeOrError()}/sms`,
        query: {
          address,
          token: response.token,
          name: attr['full_name'],
        },
      });
    };

    const onError = error => {
      if (error && error.code === 'blocked') {
        mountModal(<JoinDeviceBlockModal group={this.props.group} />, this);
        return;
      }

      if (error && error.code === 'validation_error') {
        ToastActions.showDefaultErrorToast();

        const errors = {};
        for (const field of error.field_errors) {
          const fieldName = field['field_name'];
          if (fieldName === 'uid') {
            errors[fieldName] = 'Invalid email or phone number.';
          } else {
            errors[fieldName] = field.messages.join(', ');
          }
        }

        invalidateForm(errors);
        return;
      }

      ToastActions.showDefaultErrorToast();
    };

    Subscription.initiateDeviceSubscription(this.props.group.getCode(), address).then(
      onSuccess,
      onError
    );
  };

  handleSubmit = (attr, _, invalidateForm) => {
    const address = this.state.medium + '://' + attr.uid;

    const goToSignIn = query => {
      this.context.router.push({ pathname: '/log_in', query });
      ToastActions.showSuccessToast('Looks like you already have an account. Log in below.');
    };

    const canSignIn = () => {
      // if sms, there's no password provided in form
      // we can only transition to signin page.
      if (this.state.medium === 'sms') {
        goToSignIn({ join: this.props.group.getCode() });
        return;
      }

      const onSignInFailure = () => {
        goToSignIn({
          email: attr.uid,
          join: this.props.group.getCode(),
        });
      };

      const onSignInSuccess = () => {
        this.triggerTosViewEvent();

        const onSubscribeSuccess = () => {
          JoinFlowGroupStorage.setJoinedClassName(GroupDataOps.getClassName(this.props.group));
          this.context.router.push('/classes?origin=subscribed');
          ToastActions.showSuccessToast('Class joined');
        };

        const onSubscribeFailure = error => {
          if (error && error.code === 'duplicate_membership') {
            ToastActions.showErrorToast(error.message);
            this.context.router.push(
              `/classes/${this.props.group.getCodeOrError()}?origin=subscribed`
            );
          } else {
            ToastActions.showDefaultErrorToast();
          }
        };

        GroupActions.subscribeToGroup(this.props.group).then(
          onSubscribeSuccess,
          onSubscribeFailure
        );
      };

      // if the medium is email
      // than we try to signin the user with the
      // provided password, if it works then we try
      // to subscribe the user to the group directly
      // otherwise we transition to the sign in page.
      AuthActions.signIn(attr.uid, attr.password).then(onSignInSuccess, onSignInFailure);
    };

    const cannotSignIn = () => {
      if (this.state.medium === 'sms') {
        this.continueSmsFlow(attr, invalidateForm);
      } else {
        DeviceActions.outboundVerify(attr.uid)
          .then(() => {
            mountModal(
              <LinkDeviceVerificationCodeBoxDialog
                deviceName={attr.uid}
                onRequestHide={() => {}}
                onDone={() => this.continueEmailFlow(attr, invalidateForm)}
                viewEventKey="join_group_page.device_confirmation.view"
                clickEventKey="join_group_page.device_confirmation.submit.click"
              />
            );
            ToastActions.showSuccessToast(`Sent email to ${attr.uid}`);
          }, handleSendCodeError)
          .catch((err: any) => {
            console.debug(err);
            console.error(err.toString());
            ToastActions.showDefaultErrorToast();
          });
      }
    };

    Device.belongsToFullAccount(address).then(canSignIn, cannotSignIn);
  };

  render() {
    const uidPlaceholder = this.state.i18n ? 'email@school.edu' : '(555) 555-5555';
    let uidLabel = 'Email';

    if (!this.state.i18n) {
      uidLabel = (
        <span>
          Phone number or email address
          <HelpIcon
            tooltipText={`Enter your phone number to receive messages by text or your email address to receive
              messages by email. You can always change this later.`}
          />
        </span>
      );
    }

    return (
      <div className="JoinGroupFormPanel">
        <FormDeprecated
          onSubmit={this.handleSubmit}
          onValid={() => this.setState({ canSubmit: true })}
          onInvalid={() => this.setState({ canSubmit: false })}>
          <FormInput
            label="Full name"
            required
            type="text"
            size="large"
            name="full_name"
            placeholder="First and last name"
            showErrorAfterBlur
          />
          <FormInput
            label={uidLabel}
            required
            type="text"
            name="uid"
            autoComplete="username"
            size="large"
            placeholder={uidPlaceholder}
            onChange={this.handleUidChange}
            showErrorAfterBlur
            validations={
              this.state.medium === 'email'
                ? { isEmail: true }
                : { couldPlausiblyBeANorthAmericanPhoneNumber: true }
            }
            validationErrors={{
              isEmail: 'Invalid email address.',
              couldPlausiblyBeANorthAmericanPhoneNumber: 'Invalid phone number or email address.',
            }}
          />
          {this.state.medium === 'email' && (
            <FormInput
              label="Choose a password"
              required
              key={this.state.medium}
              type="password"
              autoComplete="new-password"
              name="password"
              size="large"
              placeholder="Password"
              showErrorAfterBlur
              validations="minLength:6"
              validationErrors={{
                minLength: 'Passwords should be at least 6 characters long.',
              }}
            />
          )}
          <Button type="submit" level="primary" full disabled={!this.state.canSubmit}>
            Sign up
          </Button>
        </FormDeprecated>

        <DividerText uppercase>or</DividerText>

        <GoogleSignIn body="Sign up with Google" onSuccess={this.handleGoogleSuccess} />

        <TosText className="JoinGroupFormPanel-tos" />
      </div>
    );
  }
}

export default JoinGroupFormPanel;
